import { collectPages, EXPORT_PAGE_SIZE as PAGE } from '@/lib/collectPages';
import { formatDate } from '@/lib/format';
import type { ExportRows } from '@/types/export';
import type { FeeAccountListParams, FeeAccountRow, FeeDayBookEntry } from '@/types/fees';
import { feesApi } from './api/fees.api';
import { FEE_METHOD_LABEL, FEE_STATUS_LABEL } from './constants';

/**
 * Saving what is on screen, without a new endpoint behind it.
 *
 * The rows are already authorised — the same list the register is reading, from
 * the same scoped call — so the file is written in the browser rather than asked
 * of a server that would have to build, authorise and store it (see
 * `lib/download.ts`). Each function returns the table; the page writes it as an
 * Excel workbook (`lib/xlsx.ts`) or prints it as a PDF (`ExportTablePrint`), so
 * the two formats cannot carry different columns. What the office gets is what it filtered to: exporting the
 * whole institute from a screen showing one class is how the wrong list reaches
 * a trustee.
 *
 * Amounts are written as numbers, not as `₹1,25,000` — a spreadsheet
 * cannot add up a currency-formatted string, and adding them up is the only
 * reason anybody opens this file.
 */

/**
 * A rupee amount as a number a spreadsheet can add up.
 *
 * The API sends money as decimal strings so nothing is lost on the way; a
 * workbook cell holding `"1250.00"` is text, though, and a column of text sums
 * to nothing. Two decimal places fit a double exactly enough for every figure a
 * school prints.
 */
function rupees(value: string): number;
function rupees(value: string | null): number | null;
function rupees(value: string | null): number | null {
  return value === null ? null : Number(value);
}

/** The register, exactly as the caller has it filtered. */
export async function buildFeeRegister(filters: FeeAccountListParams): Promise<ExportRows> {
  const { items, skipped } = await collectPages<FeeAccountRow>((page) =>
    feesApi.listAccounts({ ...filters, page, limit: PAGE }),
  );

  return {
    header: [
      'Admission number',
      'Student',
      'Roll number',
      'Class',
      'Section',
      'Charged',
      'Concession',
      'Payable',
      'Collected',
      'Balance',
      'Fee status',
      'Due date',
      'Last payment',
      'Last payment amount',
    ],
    rows: items.map((account) => [
      account.admissionNumber,
      account.fullName,
      account.rollNumber ?? '',
      account.className,
      account.sectionName,
      rupees(account.charged),
      rupees(account.concession),
      rupees(account.payable),
      rupees(account.collected),
      rupees(account.balance),
      FEE_STATUS_LABEL[account.status],
      account.dueDate ? formatDate(account.dueDate) : '',
      account.lastPaidOn ? formatDate(account.lastPaidOn) : '',
      rupees(account.lastPaidAmount),
    ]),
    total: items.length + skipped,
    skipped,
  };
}

/**
 * Every receipt of the session, cancellations included.
 *
 * Cancelled receipts are exported rather than dropped, with their own column:
 * they are part of what the counter did, and a day book that quietly omits them
 * cannot be reconciled against the money in the drawer.
 */
export async function buildCollections(): Promise<ExportRows> {
  const { items, skipped } = await collectPages<FeeDayBookEntry>((page) =>
    feesApi.listPayments({ page, limit: PAGE }),
  );

  return {
    header: [
      'Receipt number',
      'Paid on',
      'Admission number',
      'Student',
      'Class',
      'Section',
      'Tuition',
      'Books and materials',
      'Other fees',
      'Total',
      'Method',
      'Reference',
      'Collected by',
      'Cancelled',
      'Cancellation reason',
    ],
    rows: items.map((receipt) => [
      receipt.receiptNumber,
      formatDate(receipt.paidOn),
      receipt.admissionNumber,
      receipt.studentName,
      receipt.className,
      receipt.sectionName,
      rupees(receipt.tuitionAmount),
      rupees(receipt.booksAmount),
      rupees(receipt.otherAmount),
      rupees(receipt.amount),
      FEE_METHOD_LABEL[receipt.method],
      receipt.reference ?? '',
      receipt.collectedBy,
      receipt.isCancelled ? 'Yes' : 'No',
      receipt.cancellationReason ?? '',
    ]),
    total: items.length + skipped,
    skipped,
  };
}
